const normalizeId = (value) =>
  value === undefined || value === null ? "" : String(value);

const normalizeGroupId = (value) => {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value).trim();
};

const getAvailableQuantity = (item, warrantyClaim = false) =>
  Number(warrantyClaim
    ? item?.warranty_claim_eligible === true ? item?.warranty_claim_remaining_qty ?? 0 : 0
    : item?.remaining_qty ?? item?.quantity);

export const createSubmissionLock = () => {
  let locked = false;

  return {
    tryAcquire() {
      if (locked) {
        return false;
      }
      locked = true;
      return true;
    },
    release() {
      locked = false;
    },
  };
};

export const reconcileReturnCartAvailability = (
  cartItems = [],
  freshOrder = {},
) => {
  const freshItemsById = new Map(
    (freshOrder?.order_items || []).map((item) => [
      normalizeId(item?.line_item_id),
      item,
    ]),
  );
  const requestedById = new Map();
  const requestTypesById = new Map();

  for (const item of cartItems) {
    const lineItemId = normalizeId(item?.line_item_id);
    const types = requestTypesById.get(lineItemId) || new Set();
    types.add(item?.return_type === "warranty_claim" ? "warranty" : "ordinary");
    requestTypesById.set(lineItemId, types);
    requestedById.set(
      lineItemId,
      (requestedById.get(lineItemId) || 0) + Number(item?.quantity || 0),
    );
  }

  const conflicts = [];
  const conflictedIds = new Set();

  for (const [lineItemId, requested] of requestedById) {
    const freshItem = freshItemsById.get(lineItemId);
    const types = requestTypesById.get(lineItemId);
    const available = getAvailableQuantity(freshItem, types.has("warranty"));

    if (
      !freshItem ||
      types.size > 1 ||
      !Number.isFinite(available) ||
      requested <= 0 ||
      requested > available
    ) {
      conflictedIds.add(lineItemId);
      conflicts.push({
        line_item_id: lineItemId,
        available: Number.isFinite(available) ? Math.max(available, 0) : 0,
        requested,
      });
    }
  }

  const conflictedGroupIds = new Set(
    cartItems
      .filter((item) =>
        conflictedIds.has(normalizeId(item?.line_item_id)),
      )
      .map((item) => normalizeGroupId(item?.product_group_id))
      .filter(Boolean),
  );

  return {
    availableItems: cartItems
      .filter(
        (item) =>
          !conflictedIds.has(normalizeId(item?.line_item_id)) &&
          !conflictedGroupIds.has(
            normalizeGroupId(item?.product_group_id),
          ),
      )
      .map((item) => {
        const lineItemId = normalizeId(item?.line_item_id);
        const freshAvailable = getAvailableQuantity(
          freshItemsById.get(lineItemId),
          item.return_type === "warranty_claim",
        );
        const requested = requestedById.get(lineItemId) || 0;

        return {
          ...item,
          remaining_qty: Math.max(freshAvailable - requested, 0),
        };
      }),
    conflicts,
  };
};

export const getCreateConflict = (error) => {
  const detail = error?.response?.data?.detail;
  const errorCode =
    detail && typeof detail === "object" ? detail.error_code : null;

  if (!["RMS033", "RMS035"].includes(errorCode)) {
    return null;
  }

  return {
    errorCode,
    additionalError:
      detail.additional_err || detail.additonal_err || null,
  };
};

export const getIykaQuoteConflict = (error) => {
  const detail = error?.response?.data?.detail;
  if (
    error?.response?.status !== 409 ||
    detail?.code !== "iyka_refund_quote_changed" ||
    !detail?.payment_details
  ) {
    return null;
  }

  return {
    message:
      detail.message || "Refund details changed. Please review the latest amount.",
    paymentDetails: detail.payment_details,
  };
};

export const hasActionableAvailabilityConflicts = (availability) =>
  Array.isArray(availability?.conflicts) &&
  availability.conflicts.length > 0;
